/* eslint-disable react/no-multi-comp */
import Link from 'next/link';
import { useRouter } from 'next/router';

import useAcl from '@/hooks/user/useAcl';
import useTranslation from '@/utils/hooks/translation/useTranslation';

import { classNames } from '@/utils/classNames';

import { aclFeatures } from '@/lib/acl/aclFeatures';
import type { ReportFormTypeConfigType } from '@/lib/reports/reportFormTypesConfig';
import reportFormTypesConfig from '@/lib/reports/reportFormTypesConfig';
import type { Url } from 'next/dist/shared/lib/router/router';

import Icon from '@/components/base/Icon';
import type { OptionType } from '@/components/base/Select';
import Select from '@/components/base/Select';
import { TooltipPlanRestriction } from '@/components/layouts/LayoutPlatform/components/Menu/components/TooltipPlanRestriction';
import ReportSectionTitle from '@/components/pages/reports/components/ReportSectionTitle';
import type { ReportFormProps } from '@/components/pages/reports/CreateEditPage/components/ReportForm';

type ReportFormHeaderProps = {
    currentType: ReportFormProps['type'];
    isEdit: ReportFormProps['isEdit'];
    isLoading: boolean;
};

type ReportFormHeaderTypeItemProps = {
    typeItem: ReportFormTypeConfigType;
    isCurrent: boolean;
    isEdit: ReportFormHeaderProps['isEdit'];
};

type ReportFormHeaderTypeItemStatusIconProps = {
    icon: string;
    className?: string;
};

const ReportFormHeaderTypeItem = ({
    typeItem,
    isCurrent,
    isEdit
}: ReportFormHeaderTypeItemProps) => {
    const { t } = useTranslation();
    const { hasAcl } = useAcl();
    const permission = 'permission' in typeItem ? typeItem.permission : null;
    const allowsPartialAccess = 'allowsPartialAccess' in typeItem && typeItem.allowsPartialAccess;

    /**
     * Check if the user has partial access to reports (not full access)
     * If the report type allows partial access and user doesn't have full access,
     * we allow them to access it even if the specific ACL would lock them out
     */
    const hasPartialAccess = allowsPartialAccess && !hasAcl(aclFeatures.report.create.full);
    const isLockedByAcl = permission?.aclRight ? !hasAcl(permission?.aclRight) : false;

    /**
     * Lock the report type only if:
     * - User is locked by the specific ACL AND
     * - User doesn't have partial access
     */
    const isLocked = isLockedByAcl && !hasPartialAccess;

    return (
        <TooltipPlanRestriction disabled={!isLocked} position="top">
            <Link href={typeItem.link} className="w-full">
                <button
                    disabled={isLocked || isEdit}
                    className={classNames(
                        'relative flex h-12 w-full min-w-150 items-center justify-center px-5',
                        'space-x-3 rounded-md text-base outline-none focus:outline-none active:outline-none',
                        {
                            'disabled:cursor-default': !isEdit,
                            'text-icoGray-500 dark:text-icoDarkMode-wolf': !isCurrent || isEdit,
                            'bg-icoGray-300 disabled:cursor-not-allowed dark:bg-white/5': isEdit,
                            'bg-icoGray-100 text-icoGray-500 dark:bg-white/5 dark:text-icoDarkMode-wolf':
                                !isCurrent,
                            'hover:bg-icoGray-200 hover:dark:bg-white/10':
                                !isLocked && !isCurrent && !isEdit,
                            'border-2': isCurrent,
                            'border-icoBlue bg-icoBlue-100 text-icoBlue dark:bg-icoBlue/20':
                                isCurrent && !isEdit,
                            'border-icoGray-500 dark:border-white/40': isCurrent && isEdit,
                            'text-opacity-40': isLocked
                        }
                    )}>
                    <Icon icon={typeItem.icon} />
                    <span>{t(typeItem.label)}</span>
                    {isLocked && <Icon icon="crown" className="text-icoBlue" />}
                    {isCurrent ? (
                        <ReportFormHeaderTypeItemStatusIcon
                            icon="check"
                            className={
                                isEdit ? 'bg-icoGray-500 dark:bg-icoDarkMode-wolf' : 'bg-icoBlue'
                            }
                        />
                    ) : null}
                </button>
            </Link>
        </TooltipPlanRestriction>
    );
};

const ReportFormHeaderTypeItemStatusIcon = ({
    icon,
    className
}: ReportFormHeaderTypeItemStatusIconProps) => (
    <div
        className={classNames(
            'absolute -right-2 -top-1.5 flex h-4 w-4 items-center justify-center rounded-xl',
            className
        )}>
        <Icon icon={icon} className="text-xxs text-white" />
    </div>
);

const ReportFormHeaderTypeSelector = ({
    currentType,
    isEdit,
    isLoading
}: ReportFormHeaderProps) => {
    return (
        <div className="hidden w-full flex-row space-x-2 lg:flex">
            {isLoading
                ? reportFormTypesConfig.map((item: ReportFormTypeConfigType) => (
                      <div
                          key={item.type}
                          className="h-14 w-full animate-pulse rounded-md bg-icoGray-300 dark:bg-icoDarkMode-anthracite"
                      />
                  ))
                : reportFormTypesConfig.map((item: ReportFormTypeConfigType) => (
                      <ReportFormHeaderTypeItem
                          key={item.type}
                          typeItem={item}
                          isCurrent={item.type === currentType}
                          isEdit={isEdit}
                      />
                  ))}
        </div>
    );
};

const ReportFormHeaderTypeMobileSelector = ({
    currentType,
    isEdit,
    isLoading
}: ReportFormHeaderProps) => {
    const { t } = useTranslation();
    const router = useRouter();
    const { hasAcl } = useAcl();

    const formatOption = (reportFormTypeConfig: ReportFormTypeConfigType) => {
        const permission =
            'permission' in reportFormTypeConfig ? reportFormTypeConfig.permission : null;
        const allowsPartialAccess =
            'allowsPartialAccess' in reportFormTypeConfig &&
            reportFormTypeConfig.allowsPartialAccess;

        const hasPartialAccess = allowsPartialAccess && !hasAcl(aclFeatures.report.create.full);
        const isLockedByAcl = permission?.aclRight ? !hasAcl(permission?.aclRight) : false;

        return {
            label: t(reportFormTypeConfig.label),
            value: reportFormTypeConfig.link,
            disabled: isLockedByAcl && !hasPartialAccess
        };
    };
    return (
        <div className="w-full lg:hidden">
            {isLoading ? (
                <div className="h-14 w-full animate-pulse rounded-md bg-icoGray-300 dark:bg-icoDarkMode-anthracite" />
            ) : (
                <Select
                    disabled={isEdit}
                    selected={formatOption(
                        reportFormTypesConfig.find((item) => item.type === currentType)
                    )}
                    setSelected={(option: OptionType) => router?.push(option.value as Url)}
                    options={reportFormTypesConfig.map((reportFormTypeConfig) =>
                        formatOption(reportFormTypeConfig)
                    )}
                />
            )}
        </div>
    );
};

const ReportFormHeader = ({
    currentType,
    isEdit = false,
    isLoading = false
}: ReportFormHeaderProps) => {
    const { t } = useTranslation();
    return (
        <>
            <ReportSectionTitle
                title={t('reports:create.report_type')}
                tooltip={t('reports:create.tooltip')}
            />
            <ReportFormHeaderTypeSelector
                currentType={currentType}
                isEdit={isEdit}
                isLoading={isLoading}
            />
            <ReportFormHeaderTypeMobileSelector
                currentType={currentType}
                isEdit={isEdit}
                isLoading={isLoading}
            />
        </>
    );
};

export default ReportFormHeader;
